/**
 * DV-TEMP-003: Demo Viewer Error Handler
 *
 * HTTP error wrapper with correlation tracking and user-friendly error mapping.
 * Provides structured errors for consistent handling across Demo pages.
 */

import { APIClientError } from './api-types';
import { DemoErrorDto, isDemoErrorDto, getDemoErrorMessage, DEMO_ERROR_CODES } from './demo-error-dto';

// Correlation headers for tracing
export interface CorrelationHeaders {
  'X-Request-Id'?: string;
  'X-Auth-Trace-Id'?: string;
  'X-Correlation-Id'?: string;
}

// Demo-specific error types with remediation hints
export interface DemoError {
  type: 'permission_denied' | 'not_found' | 'server_error' | 'network_error';
  status: number;
  message: string;
  userMessage: string;
  remediation: string[];
  endpoint: string;
  correlationId?: string;
  retryable: boolean;
  canRetryIn?: number; // milliseconds
  details?: {
    url?: string;
    method?: string;
    params?: any;
    requestId?: string;
    [key: string]: any;
  };
}

// Generate correlation ID for request tracking
function generateCorrelationId(): string {
  return `demo-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;
}

// Extract correlation ID from response headers
function extractCorrelationId(response: Response): string | undefined {
  return response.headers.get('x-correlation-id') ||
         response.headers.get('x-request-id') ||
         response.headers.get('x-auth-trace-id') ||
         undefined;
}

// Map APIClientError to DemoError with user-friendly messages
function mapApiErrorToDemoError(
  error: APIClientError,
  endpoint: string,
  correlationId?: string
): DemoError {
  const baseError: Omit<DemoError, 'type' | 'userMessage' | 'remediation' | 'retryable' | 'canRetryIn'> = {
    status: error.status,
    message: error.message,
    endpoint,
    correlationId,
    details: error.details ? {
      url: endpoint,
      method: error.details.method || 'GET',
      params: error.details.params,
      requestId: correlationId || error.details.requestId,
      ...error.details
    } : undefined,
  };

  // Permission denied (401/403)
  if (error.status === 401 || error.status === 403) {
    return {
      ...baseError,
      type: 'permission_denied',
      userMessage: error.status === 401
        ? 'Authentication required to view this data'
        : 'You do not have permission to access this execution',
      remediation: [
        'Verify you are logged in with the correct account',
        'Check that you have admin permissions in your profile',
        'Refresh the page to update your session',
        'Contact support if you believe this is an error'
      ],
      retryable: false
    };
  }

  // Not found (404)
  if (error.status === 404) {
    const isExecution = endpoint.includes('/executions/') && !endpoint.includes('/stages/');
    const isStage = endpoint.includes('/stages/');

    return {
      ...baseError,
      type: 'not_found',
      userMessage: isExecution
        ? 'Execution not found'
        : isStage
          ? 'Stage or QC data not found'
          : 'Resource not found',
      remediation: isExecution
        ? [
            'Verify the execution ID is correct',
            'Check that the execution exists in your project',
            'Use the execution selector to choose a valid execution',
            'Contact support if the execution should exist'
          ]
        : isStage
          ? [
              'Verify the stage name is correct',
              'Check that QC evaluation has been completed for this stage',
              'Try selecting a different stage',
              'Wait for the pipeline to progress further'
            ]
          : [
              'Verify the resource identifier is correct',
              'Check that the resource exists',
              'Try refreshing the page'
            ],
      retryable: false
    };
  }

  // Server errors (5xx)
  if (error.status >= 500) {
    const retryDelay = error.status === 503 ? 5000 : 2000; // 5s for 503, 2s for others

    return {
      ...baseError,
      type: 'server_error',
      userMessage: 'Server temporarily unavailable',
      remediation: [
        'This is a temporary server issue',
        'Try refreshing the page in a moment',
        'Check the system status page',
        'Contact support if the problem persists'
      ],
      retryable: true,
      canRetryIn: retryDelay
    };
  }

  // Network errors (status 0)
  if (error.status === 0) {
    return {
      ...baseError,
      type: 'network_error',
      userMessage: 'Network connection error',
      remediation: [
        'Check your internet connection',
        'Try refreshing the page',
        'Verify the server is accessible',
        'Contact support if the problem persists'
      ],
      retryable: true,
      canRetryIn: 3000
    };
  }

  // Other client errors (4xx)
  return {
    ...baseError,
    type: 'not_found', // Default to not_found for other 4xx
    userMessage: 'Request failed',
    remediation: [
      'Verify the request parameters are correct',
      'Try refreshing the page',
      'Contact support if the problem persists'
    ],
    retryable: false
  };
}

// Enhanced fetch wrapper with correlation tracking
export async function fetchWithErrorHandling(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const correlationId = generateCorrelationId();

  // Build headers respecting preflight-safe policy for GET
  const method = (options.method || 'GET').toUpperCase();
  const headers: HeadersInit = {
    ...(options.headers || {})
  };

  // NEVER set X-Request-Id on the client. For non-GET mutations, include X-Correlation-Id only.
  if (method !== 'GET') {
    (headers as Record<string, string>)['X-Correlation-Id'] = correlationId;
    // Add Content-Type for JSON bodies when not explicitly provided
    if (!('Content-Type' in (headers as Record<string, string>)) && options.body) {
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Extract any correlation ID from response
    const responseCorrelationId = extractCorrelationId(response) || correlationId;

    // Parse error response
    const errorData = await response.json().catch(() => ({
      message: `HTTP ${response.status}: ${response.statusText}`
    }));

    // Create APIClientError with correlation info
    const apiError = new APIClientError(
      errorData.message || `Request failed with status ${response.status}`,
      response.status,
      errorData.code,
      {
        ...errorData.details,
        correlationId: responseCorrelationId,
        endpoint: url
      }
    );

    // Map to DemoError
    const demoError = mapApiErrorToDemoError(apiError, url, responseCorrelationId);

    // Attach demo error info to API error for consumption
    (apiError as any).demoError = demoError;

    throw apiError;
  }

  return response;
}

// Extract DemoError from APIClientError (for hook consumption)
export function getDemoError(error: unknown): DemoError | null {
  // Handle new standardized DemoErrorDto format
  if (isDemoErrorDto(error)) {
    return mapDemoErrorDtoToLegacyFormat(error);
  }

  if (error instanceof APIClientError && (error as any).demoError) {
    return (error as any).demoError;
  }

  // Fallback for non-APIClientError
  if (error instanceof Error) {
    return {
      type: 'network_error',
      status: 0,
      message: error.message,
      userMessage: 'An unexpected error occurred',
      remediation: [
        'Try refreshing the page',
        'Check your internet connection',
        'Contact support if the problem persists'
      ],
      endpoint: 'unknown',
      retryable: true,
      canRetryIn: 3000,
      correlationId: undefined,
      details: undefined
    };
  }

  return null;
}

/**
 * Map new DemoErrorDto to legacy DemoError format for backward compatibility
 */
function mapDemoErrorDtoToLegacyFormat(errorDto: DemoErrorDto): DemoError {
  // Map error codes to legacy types
  const getLegacyType = (code: string): DemoError['type'] => {
    switch (code) {
      case DEMO_ERROR_CODES.UNAUTHORIZED:
      case DEMO_ERROR_CODES.ADMIN_ACCESS_REQUIRED:
      case DEMO_ERROR_CODES.PERMISSION_DENIED:
        return 'permission_denied';

      case DEMO_ERROR_CODES.EXECUTION_NOT_FOUND:
      case DEMO_ERROR_CODES.COMMIT_NOT_FOUND:
      case DEMO_ERROR_CODES.RESOURCE_NOT_FOUND:
        return 'not_found';

      case DEMO_ERROR_CODES.NETWORK_ERROR:
      case DEMO_ERROR_CODES.REQUEST_TIMEOUT:
      case DEMO_ERROR_CODES.REQUEST_CANCELLED:
        return 'network_error';

      default:
        return 'server_error';
    }
  };

  const getRemediation = (code: string): string[] => {
    switch (code) {
      case DEMO_ERROR_CODES.UNAUTHORIZED:
      case DEMO_ERROR_CODES.ADMIN_ACCESS_REQUIRED:
      case DEMO_ERROR_CODES.PERMISSION_DENIED:
        return [
          'Verify you are logged in with the correct account',
          'Check that you have admin permissions',
          'Refresh the page to update your session',
          'Contact support if you believe this is an error'
        ];

      case DEMO_ERROR_CODES.RESOURCE_NOT_FOUND:
        return [
          'Verify the resource identifier is correct',
          'Check that the resource exists',
          'Try refreshing the page'
        ];

      case DEMO_ERROR_CODES.NETWORK_ERROR:
        return [
          'Check your internet connection',
          'Try refreshing the page',
          'Contact support if the problem persists'
        ];

      default:
        return [
          'Try refreshing the page',
          'Contact support if the problem persists'
        ];
    }
  };

  const isRetryable = (code: string): boolean => {
    return ![
      DEMO_ERROR_CODES.UNAUTHORIZED,
      DEMO_ERROR_CODES.ADMIN_ACCESS_REQUIRED,
      DEMO_ERROR_CODES.PERMISSION_DENIED,
      DEMO_ERROR_CODES.RESOURCE_NOT_FOUND,
      DEMO_ERROR_CODES.EXECUTION_NOT_FOUND,
      DEMO_ERROR_CODES.COMMIT_NOT_FOUND,
    ].includes(code as any);
  };

  const getRetryDelay = (code: string): number | undefined => {
    switch (code) {
      case DEMO_ERROR_CODES.RATE_LIMIT_EXCEEDED:
      case DEMO_ERROR_CODES.DEMO_RATE_LIMIT_EXCEEDED:
        return 5000; // 5 seconds
      case DEMO_ERROR_CODES.SERVICE_UNAVAILABLE:
        return 10000; // 10 seconds
      default:
        return isRetryable(code) ? 3000 : undefined;
    }
  };

  return {
    type: getLegacyType(errorDto.code),
    status: errorDto.statusCode || 500,
    message: errorDto.message,
    userMessage: getDemoErrorMessage(errorDto),
    remediation: getRemediation(errorDto.code),
    endpoint: 'unknown', // Legacy format doesn't have endpoint in DTO
    correlationId: errorDto.requestId,
    retryable: isRetryable(errorDto.code),
    canRetryIn: getRetryDelay(errorDto.code),
    details: errorDto.details ? {
      requestId: errorDto.requestId,
      ...errorDto.details
    } : undefined
  };
}

// Log structured error for Demo pages
export function logDemoError(demoError: DemoError): {
  id: string;
  ts: string;
  level: 'error';
  text: string;
  meta: {
    endpoint: string;
    status: number;
    type: string;
    correlationId?: string;
    retryable: boolean;
  };
} {
  return {
    id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    ts: new Date().toISOString(),
    level: 'error',
    text: `API Error: ${demoError.userMessage}`,
    meta: {
      endpoint: demoError.endpoint,
      status: demoError.status,
      type: demoError.type,
      correlationId: demoError.correlationId,
      retryable: demoError.retryable,
    },
  };
}